import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Star, ThumbsUp, ThumbsDown, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const ReviewPage = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [reviewRequest, setReviewRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("sentiment"); // sentiment, positive, negative, complete
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [issue, setIssue] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviewRequest();
  }, [reviewId]);

  const fetchReviewRequest = async () => {
    try {
      const response = await axios.get(`${API}/reviews/request/${reviewId}`);
      setReviewRequest(response.data);
      if (response.data.status === "completed") {
        setStep("complete");
      }
    } catch (error) {
      console.error("Error fetching review request:", error);
      toast.error("Review request not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSentiment = async (sentiment) => {
    if (sentiment === "positive") {
      setStep("positive");
    } else {
      setStep("negative");
    }
  };

  const handlePositiveSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reviews/feedback`, {
        review_request_id: reviewId,
        sentiment: "positive",
        rating: rating,
        feedback_text: feedbackText,
        redirected_to_google: true
      });

      if (response.data.redirect_to_google) {
        toast.success("Thank you! Redirecting to Google Reviews...");
        // For now, show a message since we don't have the actual Google link
        setStep("complete");
        // In production, redirect to actual Google review link:
        // window.open(response.data.google_review_link, '_blank');
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNegativeSubmit = async () => {
    setSubmitting(true);
    try {
      // Submit the feedback
      await axios.post(`${API}/reviews/feedback`, {
        review_request_id: reviewId,
        sentiment: "negative",
        rating: rating,
        feedback_text: customerNotes,
        redirected_to_google: false
      });

      // Store internal feedback
      await axios.post(`${API}/reviews/internal-feedback`, {
        review_request_id: reviewId,
        customer_name: reviewRequest.customer_name,
        issue: issue,
        customer_notes: customerNotes
      });

      toast.success("Thank you for your feedback. We will reach out to resolve your concerns.");
      setStep("complete");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  if (!reviewRequest) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Review Not Found</h1>
          <p className="text-[#A0AAB2]">This review link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center px-4" data-testid="review-page">
      <div className="w-full max-w-md">
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#4A2B6E] via-[#0D5C63] to-[#FF6B35] p-0.5 mx-auto mb-4">
              <div className="w-full h-full rounded-full bg-[#0B0C10] flex items-center justify-center">
                <span className="text-2xl font-bold gradient-text">NL</span>
              </div>
            </div>
            <h1 className="text-xl font-bold text-white">New Leaf PMC</h1>
            <p className="text-[#A0AAB2] text-sm mt-1">We value your feedback</p>
          </div>

          {/* Sentiment Step */}
          {step === "sentiment" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-white mb-2">
                  Hi {reviewRequest.customer_name}!
                </h2>
                <p className="text-[#A0AAB2] text-sm">
                  Thanks for choosing New Leaf PMC for your {reviewRequest.service_completed.toLowerCase()} project.
                </p>
              </div>

              <div className="text-center">
                <p className="text-white font-medium mb-4">How was your experience?</p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => handleSentiment("positive")}
                    className="flex flex-col items-center p-6 bg-[#1F2027] border border-[#2A2B32] rounded-xl hover:border-green-500 transition-all group"
                    data-testid="sentiment-positive"
                  >
                    <ThumbsUp size={40} className="text-green-400 group-hover:scale-110 transition-transform" />
                    <span className="text-white mt-2 font-medium">Great!</span>
                  </button>
                  <button
                    onClick={() => handleSentiment("negative")}
                    className="flex flex-col items-center p-6 bg-[#1F2027] border border-[#2A2B32] rounded-xl hover:border-yellow-500 transition-all group"
                    data-testid="sentiment-negative"
                  >
                    <ThumbsDown size={40} className="text-yellow-400 group-hover:scale-110 transition-transform" />
                    <span className="text-white mt-2 font-medium">Could be better</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Positive Review Step */}
          {step === "positive" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-white mb-2">Awesome!</h2>
                <p className="text-[#A0AAB2] text-sm">
                  We are thrilled you had a great experience. Would you mind sharing your feedback on Google?
                </p>
              </div>

              {/* Star Rating */}
              <div className="text-center">
                <p className="text-white font-medium mb-3">Rate your experience</p>
                <div className="flex justify-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        size={32}
                        className={`transition-colors ${
                          star <= rating ? "text-yellow-400 fill-yellow-400" : "text-[#2A2B32]"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional feedback */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Any comments? (optional)
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35] resize-none"
                  placeholder="Tell us what you loved..."
                  data-testid="positive-feedback"
                />
              </div>

              <button
                onClick={handlePositiveSubmit}
                disabled={submitting || rating === 0}
                className="w-full gradient-btn py-4 rounded-lg font-bold flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="submit-positive"
              >
                <ExternalLink size={20} />
                <span>{submitting ? "Submitting..." : "Continue to Google Review"}</span>
              </button>
            </div>
          )}

          {/* Negative Feedback Step */}
          {step === "negative" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-white mb-2">We are sorry to hear that</h2>
                <p className="text-[#A0AAB2] text-sm">
                  Please let us know what went wrong so we can make it right.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">What was the issue?</label>
                <select
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                  className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white focus:outline-none focus:border-[#FF6B35]"
                  data-testid="issue-select"
                >
                  <option value="">Select an issue</option>
                  <option value="Quality of work">Quality of work</option>
                  <option value="Communication">Communication</option>
                  <option value="Timeliness">Timeliness</option>
                  <option value="Pricing">Pricing</option>
                  <option value="Cleanup">Cleanup</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Tell us more</label>
                <textarea
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35] resize-none"
                  placeholder="Please describe what happened..."
                  data-testid="negative-feedback"
                />
              </div>

              <button
                onClick={handleNegativeSubmit}
                disabled={submitting || !issue || !customerNotes}
                className="w-full bg-[#FF6B35] text-white py-4 rounded-lg font-bold flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="submit-negative"
              >
                <Send size={20} />
                <span>{submitting ? "Submitting..." : "Submit Feedback"}</span>
              </button>
            </div>
          )}

          {/* Complete Step */}
          {step === "complete" && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <ThumbsUp size={40} className="text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Thank You!</h2>
                <p className="text-[#A0AAB2]">
                  Your feedback helps us improve and serve you better.
                </p>
              </div>
              <button
                onClick={() => navigate("/")}
                className="text-[#FF6B35] hover:text-white font-medium transition-colors"
              >
                Return to Homepage
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
